'use client';

import { useEffect, useState } from 'react';
import { Globe } from 'lucide-react';
import { Button } from '@/components/ui/button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';

const languages = [
  { code: 'en', label: 'English', flag: '🇬🇧' },
  { code: 'am', label: 'አማርኛ', flag: '🇪🇹' },
];

const getLocaleFromCookie = () => {
  if (typeof document === 'undefined') return null;
  const match = document.cookie.match(/(?:^|; )locale=([^;]*)/);
  return match ? decodeURIComponent(match[1]) : null;
};

export function LanguageSwitcher() {
  const [mounted, setMounted] = useState(false);
  const [currentLocale, setCurrentLocale] = useState('en');

  useEffect(() => {
    setMounted(true);
    const storedLocale = getLocaleFromCookie();
    if (storedLocale) {
      setCurrentLocale(storedLocale);
    }
  }, []);

  const handleLanguageChange = (locale: string) => {
    setCurrentLocale(locale);
    document.cookie = `locale=${locale}; path=/; max-age=31536000`; // 1 year
    window.location.reload();
  };

  const currentLanguage = languages.find(lang => lang.code === currentLocale) || languages[0];

  // Prevent SSR hydration mismatch for Radix dynamic menu IDs
  if (!mounted) {
    return (
      <Button variant="outline" size="sm" className="gap-2 h-9 px-3 border-border bg-card">
        <Globe className="size-4 text-muted-foreground" />
        <span className="hidden sm:inline text-xs font-semibold text-foreground">{currentLanguage.label}</span>
        <span className="sm:hidden text-xs">{currentLanguage.flag}</span>
      </Button>
    );
  }

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button variant="outline" size="sm" className="gap-2 h-9 px-3 border-border bg-card hover:cursor-pointer">
          <Globe className="size-4 text-primary" />
          <span className="hidden sm:inline text-xs font-semibold text-foreground">{currentLanguage.label}</span>
          <span className="sm:hidden text-xs">{currentLanguage.flag}</span>
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end" className="w-36">
        {languages.map((lang) => (
          <DropdownMenuItem
            key={lang.code}
            onClick={() => handleLanguageChange(lang.code)}
            className="cursor-pointer text-xs font-medium"
          >
            <span className="mr-2">{lang.flag}</span>
            {lang.label}
          </DropdownMenuItem>
        ))}
      </DropdownMenuContent>
    </DropdownMenu>
  );
}
